import PolarisIcon from '@/components/_common/PolarisIcon';
import { selectInventorySelections, selectOutputVersions } from '@/redux/selectors/orderPlacementSelectors';
import styled from '@emotion/styled';
import { Box, Card, CardContent, CardHeader, Fade, Grid, Typography } from '@mui/material';
import { useFormikContext } from 'formik';
import { useContext, useEffect, useState } from 'react';
import OPSelectionsEmpty from '../../OPSelectionsEmpty';
import SectionHeading from '../../../_common/SectionHeading';
import TabBadge from '../../../_common/TabBadge';
import useProfile from '../../../_hooks/useProfile';
import OPOrderStepperButtons from '../../OPOrderStepperButtons';
import TitleSearchLoadingCard from '../../OPTitleSearch/TitleSearchLoadingCard';
import OPViewingInventory from '../OPViewingInventory';
import OPViewingInventoryV2 from '../OPViewingInventoryV2';
import OPOutputVersions from '../OutputVersions/OPOutputVersions';
import OPFileSelectionsPending from './OPFileSelectionsPending';
import { useAppSelector } from '@/redux/hooks';
import { FormValues } from '@/redux/slices/slice_types';
import { ConfigContext } from '@/contextAPI/configContext';

const OPStep2FileSelection = () => {
  const isTitleV2 = useContext(ConfigContext)?.titleConfig?.v2;
  const selectedInventory = useAppSelector(selectInventorySelections);
  const selectedOutputVersions = useAppSelector(selectOutputVersions);
  const [primary_version, setPrimaryVersion] = useState(null);
  const { values, setFieldValue } = useFormikContext<FormValues | undefined>();
  const { outputVersionKey, templateInputs } = useProfile(values);

  useEffect(() => {
    if (templateInputs) {
      setFieldValue(`jobs[0].title_inputs`, templateInputs);
    }
  }, []);

  useEffect(() => {
    setFieldValue(`jobs[0].title_inputs.${outputVersionKey}`, '');
  }, [primary_version]);

  return (
    <Grid container spacing={5}>
      <TitleSearchLoadingCard>{isTitleV2 ? <OPViewingInventoryV2 /> : <OPViewingInventory customerProfile={values?.jobs[0]?.profile} />}</TitleSearchLoadingCard>

      <Grid xs={12} md={5} item>
        <OutputVersionsCard elevation={0}>
          <OutputVersionsHeader
            title={
              <SectionHeading icon={<PolarisIcon icon="component_based" width={22} />} color="lavender">
                Output Versions <TabBadge count={selectedOutputVersions.length} color="lavender" />
              </SectionHeading>
            }
          />
          <CardContent sx={{ pt: 0 }}>
            {!selectedOutputVersions.length ? <OPSelectionsEmpty text="Search for a title to browse inventory" /> : <OPOutputVersions />}
          </CardContent>
        </OutputVersionsCard>
        {!!selectedInventory.length && (
          <Fade in>
            <SelectedFilesBox>
              <Typography variant="h5" sx={{ mb: 2, fontSize: '1.2rem', opacity: 0.8 }}>
                Selected Files
              </Typography>
              <FileSelectionCard added={+!!selectedInventory.length} elevation={0}>
                <CardContent>
                  <OPFileSelectionsPending primaryVersion={primary_version} setPrimaryVersion={setPrimaryVersion} />
                </CardContent>
              </FileSelectionCard>
            </SelectedFilesBox>
          </Fade>
        )}
        <Grid container>
          <Grid item sx={{ ml: 'auto', mt: 5 }}>
            <OPOrderStepperButtons />
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};

export default OPStep2FileSelection;

const OutputVersionsCard = styled(Card)`
  margin-bottom: 40px;
  overflow: visible;
`;

const FileSelectionCard = styled(Card)<{ added: number }>`
  border: 3px dashed ${({ theme, added }) => (added ? theme.palette.primary.main + '8a' : theme.palette.action.hover)};
  transition: border-color 0.2s ease-in-out;
  background: none;
  overflow: ${({ added }) => (added ? 'visible' : 'hidden')};
`;

const OutputVersionsHeader = styled(CardHeader)`
  & .MuiTypography-root > .MuiBox-root {
    margin-bottom: ${({ theme }) => theme.spacing(1)};
  }
`;

const SelectedFilesBox = styled(Box)`
  position: sticky;
  top: 70px;
`;
